/**
 * Vincent P1.8 (Eric 2026-07-31 SPEC STRICK PROD) — Extraction du hook
 * `useVideoSplitPane` depuis `VideoDetailPage.js` (2756 lignes → allègement).
 *
 * Ce hook encapsule TOUTE la logique du split-pane horizontal :
 *   - État `splitPct` (0..100) persisté dans localStorage.
 *   - État `isDragging` + listeners globaux mousemove/touchmove/up.
 *   - Ref `containerRef` à attacher sur le conteneur parent (mesure width).
 *   - Dérivé `isStacked` (splitPct ≥ 88 → mode empilé mobile-like).
 *   - Helpers :
 *       • `setSplitPctPersistent(n)` : setter qui persiste localStorage.
 *       • `toggleStacked()` : bascule 60 ↔ 100 (raccourci clavier « V »).
 *       • `resetSplit()` : remet à 60% (raccourci « C »).
 *
 * Zéro dépendance sur les autres states de VideoDetailPage → hook 100% isolé,
 * réutilisable sur d'autres pages avec split-pane.
 *
 * IMPORTANT : le hook expose les MÊMES noms de variables (`splitPct`,
 * `setSplitPct`, `isStacked`, `isDragging`, `setIsDragging`, `containerRef`)
 * pour un drop-in remplacement sans toucher au JSX. La logique d'écoute
 * clavier `V`/`C` reste dans VideoDetailPage (couplée aux autres raccourcis)
 * mais utilise les setters exposés ici.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_KEY = "knoria_video_split_pct";

function _readInitial() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const n = stored ? parseFloat(stored) : NaN;
    return !isNaN(n) && n >= 20 && n <= 100 ? n : 60;
  } catch {
    return 60;
  }
}

function _persist(n) {
  try {
    localStorage.setItem(STORAGE_KEY, String(n));
  } catch {
    /* localStorage désactivé — pas critique */
  }
}

export function useVideoSplitPane() {
  const [splitPct, setSplitPct] = useState(_readInitial);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  // Mode empilé (vidéo pleine largeur, onglets en dessous) quand splitPct ≥ 88
  const isStacked = splitPct >= 88;

  // Listeners globaux drag — actifs uniquement pendant isDragging=true
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const pct = Math.max(20, Math.min(100, (x / rect.width) * 100));
      setSplitPct(pct);
    };
    const onUp = () => {
      setIsDragging(false);
      _persist(splitPct);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging, splitPct]);

  // Helpers pour les callers (raccourcis clavier V/C, boutons).
  // Vincent P1.9 (Eric 2026-07-31 SPEC STRICK PROD) — Mémoïsés en useCallback
  // pour que les consommateurs (useEffect de raccourcis clavier dans
  // VideoDetailPage) puissent les inclure dans leurs deps sans provoquer
  // de re-attachement de listener à chaque render.
  const setSplitPctPersistent = useCallback((n) => {
    setSplitPct(n);
    _persist(n);
  }, []);
  const toggleStacked = useCallback(() => {
    setSplitPct((prev) => {
      const next = prev >= 88 ? 60 : 100;
      _persist(next);
      return next;
    });
  }, []);
  const resetSplit = useCallback(() => setSplitPctPersistent(60), [setSplitPctPersistent]);

  return {
    splitPct,
    setSplitPct,
    setSplitPctPersistent,
    isDragging,
    setIsDragging,
    isStacked,
    containerRef,
    toggleStacked,
    resetSplit,
  };
}
